import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileText, Download } from "lucide-react";

/**
 * Bulk CSV / JSON import UI for admin content modules.
 * Props:
 *   module: "pitman" | "legal" | "dictation"
 *   requiredFields: array of column names shown in the hint
 *   optionalFields: array of optional column names
 *   onImported: () => void   // refresh parent list
 */
export default function BulkImport({ module, requiredFields, optionalFields = [], onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a CSV or JSON file"); return; }
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/${module}/bulk-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.inserted} row${data.inserted === 1 ? "" : "s"} imported${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      onImported?.();
      setFile(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally { setBusy(false); }
  };

  const csvSample = () => {
    const headers = [...requiredFields, ...optionalFields];
    const example = {
      pitman:  ["Exercise 5 — Diphones", "Chapter 5", "The steno writer keeps a steady rhythm as the dictation flows."],
      legal:   ["Sample Judgment", "Bombay High Court", "The petitioner submitted that the impugned order is arbitrary...", "medium"],
      dictation: ["Legal Dictation 90 WPM", "The learned counsel argued that no notice was served on the respondent.", "90"],
    }[module] || headers.map(() => "");
    return headers.join(",") + "\n" +
           example.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",") + "\n";
  };

  const downloadSample = () => {
    const blob = new Blob([csvSample()], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${module}_sample.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-2 border-dashed border-brand-gold/40/40 p-6 space-y-4 bg-secondary/60" data-testid={`bulk-import-${module}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-brand-gold" />
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold">Bulk import — CSV or JSON</div>
        </div>
        <Button
          type="button"
          onClick={downloadSample}
          variant="outline"
          size="sm"
          className="rounded-lg border-border text-xs uppercase tracking-widest font-bold"
          data-testid={`bulk-sample-${module}`}
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Sample CSV
        </Button>
      </div>

      <div className="text-xs font-mono text-muted-foreground">
        <div>
          Required columns: <span className="font-bold">{requiredFields.join(", ")}</span>
          {optionalFields.length > 0 && (
            <> · Optional: <span className="font-bold">{optionalFields.join(", ")}</span></>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">Max 500 rows / 2 MB per file · UTF-8</div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Choose file (.csv or .json)</Label>
          <Input
            type="file"
            accept=".csv,.json,application/json,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="rounded-lg mt-2 font-mono border-border"
            data-testid={`bulk-file-${module}`}
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !file}
          className="rounded-lg h-11 bg-[#0A2540] hover:bg-brand-blue text-white text-xs uppercase tracking-[0.2em] font-bold w-full sm:w-auto"
          data-testid={`bulk-submit-${module}`}
        >
          {busy ? "Importing…" : "Import file"}
        </Button>
      </form>

      {result && (
        <div className="border border-border bg-card p-4 space-y-2" data-testid={`bulk-result-${module}`}>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground">Result</div>
          <div className="font-mono text-sm flex flex-wrap gap-4">
            <span className="text-green-700">Inserted: <strong>{result.inserted}</strong></span>
            <span className="text-yellow-700">Skipped: <strong>{result.skipped}</strong></span>
            <span className="text-muted-foreground">Received: {result.received_rows}</span>
          </div>
          {result.errors?.length > 0 && (
            <details className="text-xs font-mono text-red-700">
              <summary className="cursor-pointer">Show first {result.errors.length} error(s)</summary>
              <ul className="mt-2 space-y-0.5 pl-4 list-disc">
                {result.errors.map((e, i) => (
                  <li key={i}><FileText className="w-3 h-3 inline mr-1" />Row {e.row}: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
